import { Clock, CreditCard, PauseCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import { usePerUseActivationFee } from "@/hooks/usePerUseCardStatus";
import { useLiveCardAuthorizationStatus } from "@/hooks/useCardAuthorizationStatus";
import { formatCop } from "@/lib/credits";
import { useOrgPayers } from "@/hooks/useOrgPayers";
import { canPay } from "@/lib/billing-payer";
import { ACCOUNT_CARD_REGISTRATION_PATH, cardAuthorizationMailtoHref, cardGraceMailtoHref } from "@/lib/account-resolve";
import { ASK_ADMIN_MESSAGE, NOTIFY_ADMIN_LABEL } from "@/lib/account-standing-copy";
import { cardGraceNotice, formatGraceDayShort } from "@/lib/card-authorization-grace";
import { cn } from "@/lib/utils";

export const CARD_AUTHORIZATION_TITLE = "Tu IA está en pausa hasta que asocies una tarjeta.";
export const LINK_CARD_LABEL = "Asociar tarjeta";

export function cardAuthorizationDetail(activationFee: number): string {
  return `Tu plan de pago por uso se debita de tu tarjeta en Mercado Pago al cierre de cada ciclo, por lo que consumiste. Asociarla no genera ningún cobro ahora: Mercado Pago te mostrará ${formatCop(activationFee)} COP solo como valor de referencia. Tu equipo puede seguir atendiendo.`;
}

export function CardAuthorizationBanner() {
  const { activeWorkspaceId, userRole, workspaces } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const status = useLiveCardAuthorizationStatus(activeWorkspaceId, brand.id === "flow");
  const awaits = status?.awaitsAuthorization === true;
  const payer = canPay(userRole);
  const { payers } = useOrgPayers(activeWorkspaceId, awaits && !payer);
  const activationFee = usePerUseActivationFee(awaits);
  if (!status || !awaits) return null;

  const graceDay = !status.aiPaused ? status.graceLastDay : null;
  const inGrace = graceDay !== null;
  const orgName = workspaces.find((ws) => ws.organization_id === activeWorkspaceId)?.organization_name ?? null;
  const cardUrl = `${window.location.origin}${ACCOUNT_CARD_REGISTRATION_PATH}`;
  const notifyHref = payer
    ? null
    : inGrace
      ? cardGraceMailtoHref(payers, orgName, formatGraceDayShort(graceDay), cardUrl)
      : cardAuthorizationMailtoHref(payers, orgName, cardUrl);
  const Icon = inGrace ? Clock : PauseCircle;
  const tone = inGrace ? "text-amber-900 dark:text-amber-200" : "text-destructive";

  return (
    <div
      role={inGrace ? "status" : "alert"}
      data-testid="card-authorization-banner"
      data-tone={inGrace ? "warning" : "paused"}
      className={cn(
        "shadow-panel mx-2 mt-2 md:mx-3 md:mt-3 rounded-xl px-4 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm",
        inGrace ? "bg-amber-500/15" : "bg-destructive/10",
      )}
    >
      <div className="flex min-w-0 flex-1 basis-64 items-start gap-2">
        <Icon className={cn("h-4 w-4 shrink-0 mt-0.5", inGrace ? "text-amber-700 dark:text-amber-400" : "text-destructive")} aria-hidden="true" />
        <p className={cn("min-w-0 break-words font-medium", tone)}>
          <span className="font-bold">{inGrace ? cardGraceNotice(graceDay) : CARD_AUTHORIZATION_TITLE}</span> {cardAuthorizationDetail(activationFee)}
          {!payer && <> {ASK_ADMIN_MESSAGE}</>}
        </p>
      </div>
      {payer && (
        <button
          type="button"
          onClick={() => navigate(ACCOUNT_CARD_REGISTRATION_PATH)}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-black px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-neutral-800 dark:ring-1 dark:ring-white/30"
        >
          <CreditCard className="h-3.5 w-3.5" aria-hidden="true" /> {LINK_CARD_LABEL}
        </button>
      )}
      {notifyHref && (
        <a
          href={notifyHref}
          className={cn("shrink-0 text-xs font-medium underline underline-offset-2 hover:no-underline", tone)}
        >
          {NOTIFY_ADMIN_LABEL}
        </a>
      )}
    </div>
  );
}
